import ee from '../../services/earth-engine';
import { eeEval } from '../utils';

/**
 * Turns the modelled depth raster into vectors, so the result can leave the app as
 * GeoJSON and not only as GeoTIFF.
 * A depth surface is continuous and a GeoJSON feature is not, so something has to be chosen. This
 * module writes **one point per pixel**, carrying that pixel's predicted depth.
 * It began by writing depth bands as polygons instead, which looked right in principle - an area a
 * GIS can select and measure - and wrong in practice: the bands are cut along a quantised value,
 * so their borders are the pixel grid rather than the sea bed, and the result reads as a jigsaw
 * whose pieces do not quite meet. Points have no borders to disagree about. They also carry the
 * depth at full precision rather than rounded into a band, and a GIS interpolates, symbolises and
 * joins them directly, which is what these files are actually used for.
 * The 2 m band each point falls in travels alongside the exact depth, so colouring by class is
 * still one attribute away.
 */

/**
 * @constant DEPTH_POINT_STEP_M
 * @description Width of the depth band carried alongside each point, in metres. The same 2 m the
 * validation error is reported by, so a point in the file and a row in the report mean the same
 * thing.
 * @memberof module:Bathymetry
 */
export const DEPTH_POINT_STEP_M = 2;

/**
 * @constant DEPTH_POINT_PAGE
 * @description How many points are fetched per request.
 * Earth Engine refuses to evaluate a collection past 5000 elements - "Collection query aborted
 * after accumulating over 5000 elements" - and refuses it for the whole request, so a single call
 * for a normal area returns nothing at all rather than the first five thousand. The points are
 * therefore read a page at a time, below that ceiling.
 * @memberof module:Bathymetry
 */
export const DEPTH_POINT_PAGE = 4000;

/**
 * @constant DEPTH_POINT_MAX
 * @description How many points are written at most.
 * A GeoJSON is read into memory whole by every viewer that opens it, so an unbounded export is a
 * file that opens nowhere. Reaching this does not truncate the area: the sampling is coarsened
 * instead, so the file still covers the whole of it - see {@link fetchDepthPoints}.
 * @memberof module:Bathymetry
 */
export const DEPTH_POINT_MAX = 20000;

/**
 * @constant DEPTH_POINT_LAYER
 * @description Layer name the points are written under.
 * @memberof module:Bathymetry
 */
export const DEPTH_POINT_LAYER = 'depth_points';

/**
 * @function depthPointCollection
 * @description Samples the modelled depth at every pixel inside the area, as points.
 * Negative predictions - which a regression produces in shallow water - are dropped rather than
 * written, since a point reporting dry land as sea bed is worse than a gap.
 * The points come back in WGS84 because that is what GeoJSON is defined in; asking Earth Engine
 * for them in that projection avoids reprojecting geometry afterwards, which is where a vector
 * export usually goes wrong.
 * @param {Object} image - The modelled depth, as an `ee.Image` carrying `pred_depth`.
 * @param {Object} options
 * @param {Object} options.region - The area to sample, as an `ee.Geometry`.
 * @param {Number} options.scale - Pixel size in metres to sample at.
 * @param {Number} [options.step] - Band width, defaults to {@link DEPTH_POINT_STEP_M}.
 * **Called by:
 * @see {fetchDepthPoints}
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.FeatureCollection` of points.
 */
export const depthPointCollection = (image, { region, scale, step = DEPTH_POINT_STEP_M }) => {
  const depth = ee.Image(image).select('pred_depth').toFloat();
  const valid = depth.updateMask(depth.gt(0)).rename('depth_m');
  const band = valid.divide(step).floor().multiply(step).toInt().rename('depth_min_m');

  return valid.addBands(band).sample({
    region,
    scale,
    projection: 'EPSG:4326',
    geometries: true,
    dropNulls: true,
    tileScale: 4,
  });
};

/**
 * @function samplingScaleFor
 * @description The pixel size to sample at so the whole area fits within the cap.
 * Coarsening rather than truncating is the point: a file holding the first twenty thousand points
 * of an area covers part of it and silently stops, while a file sampled every other pixel covers
 * all of it at half the detail. The second is a map; the first is a mistake waiting to be read as
 * one.
 * Point count goes with area, so the spacing goes with its square root.
 * @param {Number} scale - The mission's own pixel size, in metres.
 * @param {Number} count - How many points that scale would produce.
 * @param {Number} [cap] - The ceiling, defaults to {@link DEPTH_POINT_MAX}.
 * @memberof module:Bathymetry
 * @returns {Number} The scale to sample at, never finer than the mission's own.
 */
export const samplingScaleFor = (scale, count, cap = DEPTH_POINT_MAX) => {
  if (!Number.isFinite(count) || count <= cap) return scale;
  return Math.ceil(scale * Math.sqrt(count / cap));
};

/**
 * @function fetchDepthPoints
 * @description Reads the modelled depth out of Earth Engine as GeoJSON points.
 * Counts first, because the count decides the sampling: an area that would produce more points
 * than the cap is sampled more coarsely so the file still covers all of it. Then reads the result
 * a page at a time, since Earth Engine will not evaluate a collection past 5000 elements.
 * @param {Object} image - The modelled depth, as an `ee.Image` carrying `pred_depth`.
 * @param {Object} options
 * @param {Object} options.region - The area to sample.
 * @param {Number} options.scale - The mission's pixel size, in metres.
 * @param {Number} [options.cap] - Ceiling on the number of points.
 * @param {Number} [options.page] - Points per request.
 * **Called by:
 * @see {ProcessingPage} - Bathymetry's result page, on the GeoJSON export.
 * @memberof module:Bathymetry
 * @returns {Promise<Object>} `{ type, features, scale, requested, coarsened }` - a GeoJSON
 *  FeatureCollection, plus what it took to get it, so the caller can say so.
 */
export const fetchDepthPoints = async (image, { region, scale, cap = DEPTH_POINT_MAX, page = DEPTH_POINT_PAGE }) => {
  const requested = await eeEval(depthPointCollection(image, { region, scale }).size());
  const sampled = samplingScaleFor(scale, requested, cap);
  const collection = depthPointCollection(image, { region, scale: sampled });

  const features = [];
  for (let offset = 0; offset < cap; offset += page) {
    const chunk = await eeEval(ee.FeatureCollection(collection.toList(page, offset)));
    const got = chunk?.features || [];
    features.push(...got);
    if (got.length < page) break;
  }

  return {
    type: 'FeatureCollection',
    features,
    scale: sampled,
    requested,
    coarsened: sampled !== scale,
  };
};
